import { getAdminOrgId } from "./admin";
import { extractApiErrorMessage, getApiBase, getAuthHeaders, BACKEND_INVENTORY_UNAVAILABLE_MSG } from "./auth";

const API_BASE = getApiBase();

function defaultHeaders(orgId: string): Record<string, string> {
  return getAuthHeaders({ orgId, json: true });
}

export type PurchaseOrderOut = {
  id: string;
  supplier_id: string;
  status: string;
  supplier_reference: string | null;
  order_date: string | null;
  expected_date: string | null;
  notes: string | null;
  composition_snapshot?: { kits?: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }[] } | null;
  created_at: string;
  updated_at: string;
};

export type PurchaseOrderLineOut = {
  id: string;
  purchase_order_id: string;
  product_id: string;
  supplier_sku: string;
  description: string | null;
  qty_ordered: number;
  qty_received: number;
  unit_cost: number;
  currency: string;
  created_at: string;
  updated_at: string;
};

export type ReceiveLineItem = {
  line_id: string;
  qty_received: number;
};

export type ReceivePayload = {
  lines: ReceiveLineItem[];
};

export type PurchaseOrderCreate = {
  supplier_id: string;
  status?: string;
  supplier_reference?: string | null;
  order_date?: string | null;
  expected_date?: string | null;
  notes?: string | null;
  composition_snapshot?: { kits?: { name: string; quantity: number; lines: { product_id: string; qty_per_kit: number }[] }[] } | null;
};

export type PurchaseOrderLineCreate = {
  product_id: string;
  supplier_sku: string;
  description?: string | null;
  qty_ordered: number;
  qty_received?: number;
  unit_cost: number;
  currency: string;
};

/** One line in a kit (product + qty per kit), before decomposition */
export type ComputeKitLineItem = {
  product_id: string;
  qty_per_kit: number;
};

export type ComputeKitInput = {
  name: string;
  quantity: number;
  lines: ComputeKitLineItem[];
};

export type ComputeLinesRequest = {
  supplier_id: string;
  kits: ComputeKitInput[];
};

export type ConsolidatedLineOut = {
  product_id: string;
  supplier_sku: string;
  description: string | null;
  qty: number;
  unit_cost: number;
  currency: string;
  sources: string[];
  /** True = bundle as a whole (count in total) */
  is_bundle?: boolean;
  /** If set = component of this bundle (display only, do not count in total) */
  parent_bundle_id?: string | null;
};

export type ComputeLinesResponse = {
  consolidated_lines: ConsolidatedLineOut[];
};

export async function computePurchaseOrderLines(payload: ComputeLinesRequest): Promise<ComputeLinesResponse> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/compute-lines`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to compute lines"));
  }
  return response.json();
}

export async function createPurchaseOrder(payload: PurchaseOrderCreate): Promise<PurchaseOrderOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to create purchase order"));
  }
  return response.json();
}

export async function updatePurchaseOrder(poId: string, payload: Partial<PurchaseOrderCreate>): Promise<PurchaseOrderOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to update purchase order"));
  }
  return response.json();
}

export async function deletePurchaseOrder(poId: string): Promise<void> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}`, {
    method: "DELETE",
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to delete purchase order"));
  }
}

export type PurchaseOrderLineUpdate = {
  qty_ordered?: number;
  qty_received?: number;
  unit_cost?: number;
  currency?: string;
  description?: string | null;
};

export async function createPurchaseOrderLine(
  poId: string,
  payload: PurchaseOrderLineCreate
): Promise<PurchaseOrderLineOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}/lines`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify({ ...payload, qty_received: payload.qty_received ?? 0 }),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to add line to purchase order"));
  }
  return response.json();
}

export async function updatePurchaseOrderLine(
  poId: string,
  lineId: string,
  payload: PurchaseOrderLineUpdate
): Promise<PurchaseOrderLineOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}/lines/${lineId}`, {
    method: "PATCH",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to update PO line"));
  }
  return response.json();
}

export async function listPurchaseOrders(params?: {
  supplier_id?: string;
  status?: string;
  page?: number;
  page_size?: number;
}): Promise<PurchaseOrderOut[]> {
  const orgId = await getAdminOrgId();
  const queryParams = new URLSearchParams();
  if (params?.supplier_id) queryParams.append("supplier_id", params.supplier_id);
  if (params?.status) queryParams.append("status", params.status);
  if (params?.page) queryParams.append("page", params.page.toString());
  if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
  const response = await fetch(
    `${API_BASE}/purchase-orders?${queryParams.toString()}`,
    { headers: defaultHeaders(orgId) }
  );
  if (!response.ok) {
    if (response.status === 404 || response.status === 484) throw new Error(BACKEND_INVENTORY_UNAVAILABLE_MSG);
    throw new Error(await extractApiErrorMessage(response, "Failed to load purchase orders"));
  }
  return response.json();
}

export async function getPurchaseOrder(poId: string): Promise<PurchaseOrderOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to load purchase order"));
  }
  return response.json();
}

export async function listPurchaseOrderLines(poId: string): Promise<PurchaseOrderLineOut[]> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}/lines`, {
    headers: defaultHeaders(orgId),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to load PO lines"));
  }
  return response.json();
}

export async function receivePurchaseOrder(
  poId: string,
  payload: ReceivePayload
): Promise<PurchaseOrderOut> {
  const orgId = await getAdminOrgId();
  const response = await fetch(`${API_BASE}/purchase-orders/${poId}/receive`, {
    method: "POST",
    headers: defaultHeaders(orgId),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await extractApiErrorMessage(response, "Failed to receive"));
  }
  return response.json();
}
